<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SectionLayout from './SectionLayout.vue';

defineProps({
  fullWidth: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const timeoutMinutes = ref('');
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

watch(
  currentAccount,
  () => {
    const { user_inactivity_timeout } = currentAccount.value?.settings || {};
    timeoutMinutes.value =
      user_inactivity_timeout != null ? String(user_inactivity_timeout) : '';
  },
  { deep: true, immediate: true }
);

const saveTimeout = async () => {
  const parsed = parseInt(timeoutMinutes.value, 10);
  if (
    timeoutMinutes.value !== '' &&
    (Number.isNaN(parsed) || parsed < 1 || parsed > 60)
  ) {
    useAlert(t('GENERAL_SETTINGS.FORM.USER_INACTIVITY_TIMEOUT.ERROR'));
    return;
  }

  isSubmitting.value = true;
  try {
    await updateAccount({
      user_inactivity_timeout: timeoutMinutes.value === '' ? null : parsed,
    });
    useAlert(t('GENERAL_SETTINGS.FORM.USER_INACTIVITY_TIMEOUT.API.SUCCESS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.FORM.USER_INACTIVITY_TIMEOUT.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.USER_INACTIVITY_TIMEOUT.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.USER_INACTIVITY_TIMEOUT.NOTE')"
    class="!pt-0"
  >
    <form
      class="grid w-full gap-4"
      :class="{ 'max-w-md': !fullWidth }"
      @submit.prevent="saveTimeout"
    >
      <WithLabel
        name="user-inactivity-timeout"
        :label="t('GENERAL_SETTINGS.FORM.USER_INACTIVITY_TIMEOUT.LABEL')"
      >
        <NextInput
          v-model="timeoutMinutes"
          type="number"
          min="1"
          max="60"
          class="w-full"
          :placeholder="
            t('GENERAL_SETTINGS.FORM.USER_INACTIVITY_TIMEOUT.PLACEHOLDER')
          "
        />
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('GENERAL_SETTINGS.FORM.USER_INACTIVITY_TIMEOUT.HELP') }}
        </p>
      </WithLabel>
      <div>
        <NextButton blue :is-loading="isSubmitting" type="submit">
          {{ t('GENERAL_SETTINGS.SUBMIT') }}
        </NextButton>
      </div>
    </form>
  </SectionLayout>
</template>
